import { Dispatch, SetStateAction, useMemo } from 'react';

import {
  ALL_OPTION,
  Box,
  Button,
  Checkbox,
  DataLoaderWithRetry,
  Flex,
  Headings,
  OptionType,
  Select,
  useCheckbox,
  useIsDeviceSize,
  useSelectMulti,
  useSelectSingle,
} from '@foradevelopment/fora-ui';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';
import {
  AdministrableEmployerViewModel,
  ReportedPayrollsRequestInput,
  ReportsRequestInput,
} from '@fora-monorepo/employer-my-pages-common/types';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  getEmployeesWithReportedPayroll,
  getPayHistory,
  getReportingPeriods,
} from '../../redux/payHistory/payHistoryThunks';
import {
  getAdministrators,
  getPeriodForReportHistory,
  getReportHistory,
} from '../../redux/reportHistory/reportHistoryThunks';
import { useTypedTranslation } from '../../i18n/i18n';
import { allAdministratorsOptions, allIndividualsOption, allMonthsOption } from '../../common/utils';

type HistoryFiltersProps = {
  changeFilter?: Dispatch<SetStateAction<ReportedPayrollsRequestInput>>;
  variant: 'pay' | 'report';
};

export const HistoryFilters = ({ changeFilter, variant }: HistoryFiltersProps) => {
  const { t } = useTypedTranslation();
  const { isMobile, isTablet } = useIsDeviceSize();
  const dispatch = useAppDispatch();

  const isLoadingEmployees = useAppSelector(state => state.payHistory.loadingEmployeesWithReportedPayroll);
  const isLoadingReportingPeriods = useAppSelector(state => state.payHistory.loadingReportingPeriods);
  const isLoadingPeriodsForReportHistory = useAppSelector(state => state.reportHistory.loadingPeriodsForReportHistory);
  const isLoadingAdministrators = useAppSelector(state => state.reportHistory.loadingAdministrators);

  const administrableEmployers = useAppSelector(state => state.employer.administrableEmployers);
  const employerInfo = useAppSelector(state => state.employer.employerInfo);
  const employeesWithPayrollOptions = useAppSelector(state => state.payHistory.employeesWithReportedPayrollOptions);
  const payHistoryMonths = useAppSelector(state => state.payHistory.reportingPeriodOptions);
  const administratorOptions = useAppSelector(state => state.reportHistory.administratorOptions);
  const reportHistoryMonths = useAppSelector(state => state.reportHistory.reportingPeriodOptions);

  const payMonthsInput = useSelectMulti([allMonthsOption.id], true);
  const individualsInput = useSelectSingle(allIndividualsOption.id);
  const administratorsInput = useSelectSingle(allAdministratorsOptions.id);
  const onlyReportsWithDeviationsInput = useCheckbox(false);

  const handleOrganizationChange = (partnerNumberOrganization: string, valueChanged?: boolean) => {
    if (valueChanged) {
      if (variant === 'pay') {
        dispatch(getEmployeesWithReportedPayroll({ partnerNumberOrganization }));
        dispatch(getReportingPeriods({ partnerNumberOrganization }));
      } else {
        dispatch(getPeriodForReportHistory({ partnerNumberOrganization }));
        dispatch(getAdministrators({ partnerNumberOrganization }));
      }
      individualsInput.props.setValue(allIndividualsOption.id);
      administratorsInput.props.setValue(allAdministratorsOptions.id);
      payMonthsInput.props.setValue([allMonthsOption.id]);
    }
  };

  const organizationInput = useSelectSingle(employerInfo.partnerNumber, handleOrganizationChange);

  const disableInput = useMemo(() => {
    if (variant === 'pay') {
      return (
        !organizationInput.props.value ||
        isLoadingEmployees === ThunkRequestStatus.Pending ||
        isLoadingReportingPeriods === ThunkRequestStatus.Pending
      );
    } else {
      return (
        !organizationInput.props.value ||
        isLoadingAdministrators === ThunkRequestStatus.Pending ||
        isLoadingPeriodsForReportHistory === ThunkRequestStatus.Pending
      );
    }
  }, [
    variant,
    organizationInput.props.value,
    isLoadingEmployees,
    isLoadingReportingPeriods,
    isLoadingAdministrators,
    isLoadingPeriodsForReportHistory,
  ]);

  const administrableEmployersOptions = useMemo(
    () => getAdministrableEmployers(administrableEmployers),
    [administrableEmployers]
  );
  const payMonthOptions = useMemo(
    () => (variant === 'pay' ? payHistoryMonths : reportHistoryMonths),
    [payHistoryMonths, reportHistoryMonths, variant]
  );

  const handleFilterSubmit = () => {
    if (variant === 'pay') {
      const filters: ReportedPayrollsRequestInput = {
        payMonths: getPayMonthsFromPeriodOption(payMonthsInput.props.value, payMonthOptions),
        partnerNumberIndividuals: individualsInput.props.value === ALL_OPTION ? [] : [individualsInput.props.value],
        partnerNumberOrganization: organizationInput.props.value ?? '',
      };
      changeFilter?.(filters);
      dispatch(getPayHistory(filters));
    } else if (variant === 'report') {
      const filters: ReportsRequestInput = {
        payMonths: getPayMonthsFromPeriodOption(payMonthsInput.props.value, payMonthOptions),
        reporters: administratorsInput.props.value === ALL_OPTION ? [] : [administratorsInput.props.value],
        onlyReportsWithDeviations: onlyReportsWithDeviationsInput.checked,
        partnerNumberOrganizations: [organizationInput.props.value ?? ''],
      };
      dispatch(getReportHistory(filters));
    }
  };

  return (
    <Box
      maxWidth={['100%', '100%', administrableEmployers.length === 1 ? 784 : '100%']}
      backgroundColor="white"
      paddingBottom={40}
      paddingLeft={isMobile ? 20 : 40}
      paddingRight={isMobile ? 20 : 40}
      paddingTop={40}
      marginBottom={40}
    >
      <Headings.H3 marginBottom={20}>{t('filter.title')}</Headings.H3>
      <Flex
        flexDirection={isMobile ? 'column' : 'row'}
        columnGap={isTablet ? 20 : 40}
        marginBottom={40}
        rowGap={isMobile ? 20 : 0}
        maxWidth={'100%'}
      >
        {administrableEmployers.length > 1 && (
          <Box width={['100%', '30%']} flexShrink={0} flexGrow={1}>
            <Select
              {...organizationInput.props}
              searchPlaceholder={t('common.searchShortLabel')}
              label={t('section.reportHistory.selectOrganizationAccessibilityLabel')}
              options={administrableEmployersOptions}
              search={administrableEmployersOptions.length > 6}
              itemsHaveDescription
              testId="organization"
            />
          </Box>
        )}
        <Box width={['100%', '30%']} flexShrink={0} flexGrow={1} style={{ alignContent: 'flex-end' }}>
          {variant === 'report' &&
          (isLoadingPeriodsForReportHistory === ThunkRequestStatus.Rejected ||
            isLoadingPeriodsForReportHistory === ThunkRequestStatus.Pending) ? (
            <DataLoaderWithRetry
              type="input"
              isLoading={isLoadingPeriodsForReportHistory === ThunkRequestStatus.Pending}
              backgroundColor="black05"
              onClick={() =>
                dispatch(getPeriodForReportHistory({ partnerNumberOrganization: organizationInput.props.value }))
              }
            />
          ) : variant === 'pay' &&
            (isLoadingReportingPeriods === ThunkRequestStatus.Rejected ||
              isLoadingReportingPeriods === ThunkRequestStatus.Pending) ? (
            <DataLoaderWithRetry
              type="input"
              isLoading={isLoadingReportingPeriods === ThunkRequestStatus.Pending}
              backgroundColor="black05"
              onClick={() =>
                dispatch(getReportingPeriods({ partnerNumberOrganization: organizationInput.props.value }))
              }
            />
          ) : (
            <Select
              {...payMonthsInput.props}
              search={payMonthOptions.length > 6}
              label={t('section.reportHistory.selectPeriodAccessibilityLabel')}
              searchPlaceholder={t('common.searchShortLabel')}
              options={payMonthOptions}
              disabled={disableInput}
              testId="months"
            />
          )}
        </Box>
        {variant === 'pay' && (
          <Box width={['100%', '30%']} flexShrink={0} flexGrow={1} style={{ alignContent: 'flex-end' }}>
            {isLoadingEmployees === ThunkRequestStatus.Rejected || isLoadingEmployees === ThunkRequestStatus.Pending ? (
              <DataLoaderWithRetry
                type="input"
                isLoading={isLoadingEmployees === ThunkRequestStatus.Pending}
                backgroundColor="black05"
                onClick={() =>
                  dispatch(
                    getEmployeesWithReportedPayroll({ partnerNumberOrganization: organizationInput.props.value })
                  )
                }
              />
            ) : (
              <Select
                {...individualsInput.props}
                label={t('section.payrollHistory.selectIndividualAccessibilityLabel')}
                search={employeesWithPayrollOptions.length > 6}
                searchPlaceholder={t('common.searchShortLabel')}
                options={employeesWithPayrollOptions}
                disabled={disableInput}
                itemsHaveDescription
                testId="employees"
              />
            )}
          </Box>
        )}
        {variant === 'report' && (administrableEmployers.length !== 0 || administratorOptions.length > 1) && (
          <Box width={['100%', '30%']} flexShrink={0} flexGrow={1} style={{ alignContent: 'flex-end' }}>
            {isLoadingAdministrators === ThunkRequestStatus.Rejected ||
            isLoadingAdministrators === ThunkRequestStatus.Pending ? (
              <DataLoaderWithRetry
                type="input"
                isLoading={isLoadingAdministrators === ThunkRequestStatus.Pending}
                backgroundColor="black05"
                onClick={() =>
                  dispatch(getAdministrators({ partnerNumberOrganization: organizationInput.props.value }))
                }
              />
            ) : (
              <Select
                {...administratorsInput.props}
                label={t('section.reportHistory.selectAdminAccessibilityLabel')}
                searchPlaceholder={t('common.searchShortLabel')}
                options={administratorOptions}
                search={administratorOptions.length > 5}
                itemsHaveDescription
                disabled={disableInput}
                testId="administrators"
              />
            )}
          </Box>
        )}
      </Flex>
      <Flex
        justifyContent={variant === 'report' ? 'space-between' : 'flex-end'}
        alignContent="center"
        flexDirection={isMobile ? 'column' : 'row'}
        rowGap={40}
      >
        {variant === 'report' && (
          <Flex alignItems="center">
            <Checkbox
              id="show-reports-with-deviations-checkbox"
              {...onlyReportsWithDeviationsInput}
              children={undefined}
              disabled={disableInput}
            />
            {t('filter.checkboxLabels.deviations')}
          </Flex>
        )}

        <Box>
          <Button variant="primary" onClick={() => handleFilterSubmit()} disabled={disableInput}>
            {t('common.filter.buttonUpdate')}
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

const getPayMonthsFromPeriodOption = (options: string[], payMonthsOptions: OptionType[]): string[] => {
  // const year = payMonthsOptions[0]?.id.slice(0, 4);
  switch (options[0]) {
    case ALL_OPTION:
      return [];
    // Save options if needed in the future
    // case LAST_MONTH:
    //   return payMonthsOptions.slice(0, 1);
    // case LAST_THREE_MONTHS:
    //   return payMonthsOptions.slice(0, 3);
    // case LAST_SIX_MONTHS:
    //   return payMonthsOptions.slice(0, 6);
    // case LAST_YEAR:
    //   return payMonthsOptions.filter(e => e.id.includes(year));
    default:
      return options;
  }
};

const getAdministrableEmployers = (options: AdministrableEmployerViewModel[]): OptionType[] => {
  return options.map(x => {
    return {
      id: x.partnerNumber,
      name: x.organizationName,
      description: x.organizationNumber,
    };
  });
};
